<template>
	<div class="container-md mh-100">
		<LayoutHeaderSection>
			<template #title>
				<LayoutTextSection :text="(customer?.id) ? `Propuesta para ${customer.name}` : '...'" :breadcrumb="true" />
			</template>
		</LayoutHeaderSection>
		<main class="pt-8 vstack gap-10">
			<CommonFormWrapper submitText="Guardar" :cancelUrl="{ name: 'customer.index' }" :submitting="submitting" @submit="submit" v-if="!loading">
				<template #body>
					<CommonFormInputSelect v-model="target.synchronizer_id" title="Mediante" placeholder="Sin especificar" :options="synchronizersOptions" v-if="synchronizersOptions.length > 1" />
					<CommonFormInputText v-model="target.name" title="Nombre descriptivo" subtitle="Opcional" placeholder="Ingrese un nombre descriptivo" />
					<table class="table">
						<thead>
							<tr class="fw-bold">
								<td class="text-sm">
									<div class="hstack gap-2 justify-content-between">
										<div>Detalles de productos</div>
										<button type="button" class="btn btn-xs btn-dark rounded-pill flex-shrink-0" :disabled="!customer?.company_id" @click="show">Agregar productos</button>
									</div>
								</td>
							</tr>
						</thead>
						<tbody>
							<template v-if="target.products.length > 0">
								<PropostalTableRow v-for="(product, index) in target.products" v-bind:key="index" :index="index" :product="product" @increase="increase" @decrease="decrease" @remove="remove" />
							</template>
							<template v-else>
								<tr>
									<td class="text-sm">No hay productos.</td>
								</tr>
							</template>
						</tbody>
						<tfoot v-if="totals.length > 0">
							<tr class="fw-bold">
								<td class="text-md align-top">
									<div class="hstack gap-2 justify-content-between align-items-start">
										<div>Totales</div>
										<div class="vstack text-end">
											<div v-for="(total, index) in totals" v-bind:key="index">{{ vueNumberFormat(total.amount, { suffix: ` ${total.currency}`, precision: 2 }) }}</div>
										</div>
									</div>
								</td>
							</tr>
						</tfoot>
					</table>
				</template>
			</CommonFormWrapper>
		</main>

		<ProductModalAdd ref="modalProduct" :company_id="customer?.company_id" @add="add" />
	</div>
</template>

<script>
	import { ref, reactive, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue';
	import router from '@/router';
	import _ from 'lodash';
	import moment from 'moment';
	import { useRoute } from 'vue-router';
	import { useToast } from 'vue-toastification';
	import composableForm from '@/composables/form';
	import composableRedirect from '@/composables/redirect';
	import SynchronizerRepository from '@/repositories/modules/Synchronizer';
	import CustomerRepository from '@/repositories/modules/Customer';
	import ProposalRepository from '@/repositories/modules/Proposal';

	export default {
		props: {
			id: String
		},
		setup(props) {
			const { loading, submitting, catchError } = composableForm();
			const { redirectBack } = composableRedirect();
			const instance = getCurrentInstance();
			const route = useRoute();
			const toast = useToast();
			const synchronizers = ref([]);
			const synchronizersOptions = computed(() => _.mapValues(_.keyBy(synchronizers.value, 'id'), 'name'));
			const customer = ref({});
			const target = reactive({
				customer_user_id: null,
				synchronizer_id: null,
				type: 'proposal:order',
				name: null,
				products: []
			});

			const totals = computed(() => {
				const grouped = _.groupBy(target.products, 'currency');
				return Object.keys(grouped).map((currency) => ({
					currency,
					amount: _.sumBy(grouped[currency], (item) => (Math.round((item.amount * item.pivot.quantity) * 100) / 100))
				}));
			});

			const submit = async () => {
				if(submitting.value) return;
				submitting.value = true;

				await ProposalRepository.store(target).then((response) => {
					instance.proxy.$customerProposed(customer.value, response.data);
				}).catch(catchError);

				submitting.value = false;
			}

			const modalProduct = ref(null);
			const show = () => modalProduct.value.show(target);

			const add = (data) => {
				if(data.pivot.stock <= 0) {
					toast.error(`El producto ${data.name} no posee stock.`);
				}else if(!_.some(target.products, (product) => { return (product.id == data.id && _.isEqual(product.pivot, data.pivot)); })) {
					target.products.push(data);
					toast.success(`El producto ${data.name} fué cargado correctamente.`);
				}else{
					toast.warning(`El producto ${data.name} ya se encontraba cargado.`);
				}
			}

			const increase = (index) => target.products[index].pivot.quantity++;
			const decrease = (index) => target.products[index].pivot.quantity--;
			const remove = (index) => target.products.splice(index, 1);

			onMounted(async () => {
				try {
					const [
						synchronizerResponse,
						customerResponse
					] = await Promise.all([
						SynchronizerRepository.list({ company_id: route.query.company_id, capability: target.type }),
						CustomerRepository.show(route.query.customer_id)
					]);

					synchronizers.value = synchronizerResponse.data;
					customer.value = customerResponse.data;

					target.customer_user_id = customer.value.pivot.id;
					if(synchronizers.value.length == 1) target.synchronizer_id = synchronizers.value[0].id;
				}catch(e) {
					catchError(e, true);
					redirectBack();
				}finally {
					loading.value = false;
				}

				if(instance) instance.appContext.config.globalProperties.$redirect = () => {
					router.push({ name: 'customer.index' });
				}
			});

			onUnmounted(() => {
				if(instance && instance.appContext.config.globalProperties.$redirect) {
					delete instance.appContext.config.globalProperties.$redirect;
				}
			});

			return { loading, synchronizersOptions, customer, target, submit, submitting, modalProduct, show, add, increase, decrease, remove, totals };
		}
	}
</script>